import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { ColorPicker } from '../components/ui/ColorPicker';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import { Sheet } from '../components/ui/Sheet';
import { toast } from 'sonner';
import { apiFetch, parseJsonResponse, isNetworkErrorResponse } from '../utils/api';
import { ComboBannerForm } from '../components/ComboBannerForm';
import { SliderImageManager } from '../components/SliderImageManager';

interface Screen {
  id: number;
  establishment_id: number;
  name: string;
  url_token: string;
  template_id: number;
  is_active: number;
  orientation: 'horizontal' | 'vertical';
  content_type: 'menu_only' | 'banners_only' | 'menu_and_banners' | 'advertising_only';
  banner_side: 'left' | 'right' | null;
  banner_divisions: number;
  products_count: number;
  color_background?: string;
  color_title?: string;
  color_subtitle?: string;
  color_text?: string;
  color_price_text?: string;
  color_price_number?: string;
  font_size_title?: number;
  font_size_subtitle?: number;
  font_size_text?: number;
  font_size_price_text?: number;
  font_size_price_number?: number;
  font_title_id?: number | null;
  font_subtitle_id?: number | null;
  font_text_id?: number | null;
  font_script_id?: number | null;
  font_price_text_id?: number | null;
  font_price_number_id?: number | null;
  last_accessed: string | null;
  access_count: number;
  banner_duration: number;
  banner_transition: number;
  banner_auto_play: number;
  banner_loop: number;
  created_at: string;
  updated_at: string;
}

interface Font {
  id: number;
  name: string;
  display_name: string;
  font_family: string;
  is_google_font: number;
  google_font_url: string | null;
}

interface Establishment {
  id: number;
  name_establishment: string;
  code_establishment_hioffice: number;
  is_active: number;
}

interface Banner {
  id: number;
  name: string;
  image_path: string;
  is_active: number;
}

interface SelectedBanner {
  banner_id: number;
  position: 'superior' | 'lateral';
  lateral_position?: number;
  display_order?: number;
}

interface SmartBanner {
  id: number;
  name: string;
  is_active: number;
}

interface SelectedSmartBanner {
  smart_banner_id: number;
  position: 'superior' | 'lateral';
  lateral_position?: number;
  display_order?: number;
}

interface ProductVariant {
  product_id: number;
  name: string;
  variant_label: string;
  price: number;
  image_url?: string | null;
  tiene_aguacate?: number;
  es_picante?: number;
  es_recomendado?: number;
  description?: string | null;
}

interface ProductReference {
  reference_code: string;
  base_name: string;
  family?: string | null;
  subcategoria?: string | null;
  variants: ProductVariant[];
}

interface Template {
  id: number;
  name: string;
  description: string | null;
  preview_image: string | null;
  orientation: 'horizontal' | 'vertical';
  content_type: 'menu_only' | 'banners_only' | 'menu_and_banners' | 'advertising_only';
  banner_side: 'left' | 'right' | null;
  banner_divisions: number;
  products_count: number;
  color_background: string;
  color_title: string;
  color_subtitle: string;
  color_text: string;
  color_price_text: string;
  color_price_number: string;
  font_size_title: number;
  font_size_subtitle: number;
  font_size_text: number;
  font_size_price_text: number;
  font_size_price_number: number;
  font_title_id: number | null;
  font_subtitle_id: number | null;
  font_text_id: number | null;
  font_script_id: number | null;
  font_price_text_id: number | null;
  font_price_number_id: number | null;
}

export const Screens: React.FC = () => {
  const { token } = useAuth();
  const { can, canView } = usePermissions();
  const [screens, setScreens] = useState<Screen[]>([]);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEstablishments, setLoadingEstablishments] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingScreen, setEditingScreen] = useState<Screen | null>(null);
  const [availableBanners, setAvailableBanners] = useState<Banner[]>([]);
  const [loadingBanners, setLoadingBanners] = useState(false);
  const [selectedBanners, setSelectedBanners] = useState<{
    superior: (number | null)[];
    laterales: (number | null)[];
  }>({
    superior: [null, null],
    laterales: []
  });
  const [availableSmartBanners, setAvailableSmartBanners] = useState<SmartBanner[]>([]);
  const [loadingSmartBanners, setLoadingSmartBanners] = useState(false);
  const [selectedSmartBanners, setSelectedSmartBanners] = useState<{
    superior: number | null;
    laterales: (number | null)[];
  }>({
    superior: null,
    laterales: []
  });
  const [availableReferences, setAvailableReferences] = useState<ProductReference[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedReferences, setSelectedReferences] = useState<ProductReference[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [fonts, setFonts] = useState<Font[]>([]);
  const [loadingFonts, setLoadingFonts] = useState(false);
  const [comboBanners, setComboBanners] = useState<any[]>([]);
  const [loadingComboBanners, setLoadingComboBanners] = useState(false);
  const [showComboBannerForm, setShowComboBannerForm] = useState(false);
  const [editingComboBanner, setEditingComboBanner] = useState<any | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showCopyStylesModal, setShowCopyStylesModal] = useState(false);
  const [availableScreensForCopy, setAvailableScreensForCopy] = useState<Screen[]>([]);
  const [loadingScreensForCopy, setLoadingScreensForCopy] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    orientation: 'horizontal' as 'horizontal' | 'vertical',
    content_type: 'menu_and_banners' as 'menu_only' | 'banners_only' | 'menu_and_banners' | 'advertising_only',
    slider_type: 'fade' as 'fade' | 'slide' | 'zoom',
    banner_side: 'left' as 'left' | 'right' | null,
    banner_divisions: 2,
    products_count: 6,
    color_background: '#ffffff',
    color_title: '#000000',
    color_subtitle: '#333333',
    color_text: '#666666',
    color_price_text: '#333333',
    color_price_number: '#333333',
    font_size_title: 24,
    font_size_subtitle: 18,
    font_size_text: 14,
    font_size_price_text: 16,
    font_size_price_number: 16,
    font_title_id: null as number | null,
    font_subtitle_id: null as number | null,
    font_text_id: null as number | null,
    font_script_id: null as number | null,
    font_price_text_id: null as number | null,
    font_price_number_id: null as number | null
  });
  const [activeTab, setActiveTab] = useState<'config' | 'banners' | 'products' | 'styles' | 'slider-images'>('config');

  // Cargar fuentes
  useEffect(() => {
    const loadFonts = async () => {
      if (!token) {
        return;
      }

      setLoadingFonts(true);
      try {
        const route = 'fonts/list';
        const res = await apiFetch(route, {}, token);
        
        if (isNetworkErrorResponse(res)) {
          return;
        }

        const json = await parseJsonResponse(res);

        if (!res.ok || json.error) {
          return;
        }

        setFonts(json.data?.fonts || []);
      } catch (err: any) {
        console.error('Error cargando fuentes:', err);
      } finally {
        setLoadingFonts(false);
      }
    };

    loadFonts();
  }, [token]);

  // Cargar plantillas
  const loadTemplates = async () => {
    if (!token) {
      return;
    }

    setLoadingTemplates(true);
    try {
      const route = 'templates/list';
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        return;
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        return;
      }

      setTemplates(json.data?.templates || []);
    } catch (err: any) {
      console.error('Error cargando plantillas:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Cuando se selecciona una plantilla, cargar sus valores
  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
    setFormData({
      name: '',
      orientation: template.orientation,
      content_type: template.content_type,
      slider_type: (template as any).slider_type || 'fade',
      banner_side: template.banner_side || 'left',
      banner_divisions: template.banner_divisions,
      products_count: template.products_count,
      color_background: template.color_background,
      color_title: template.color_title,
      color_subtitle: template.color_subtitle,
      color_text: template.color_text,
      color_price_text: template.color_price_text,
      color_price_number: template.color_price_number,
      font_size_title: template.font_size_title,
      font_size_subtitle: template.font_size_subtitle,
      font_size_text: template.font_size_text,
      font_size_price_text: template.font_size_price_text,
      font_size_price_number: template.font_size_price_number,
      font_title_id: template.font_title_id,
      font_subtitle_id: template.font_subtitle_id,
      font_text_id: template.font_text_id,
      font_script_id: template.font_script_id,
      font_price_text_id: template.font_price_text_id,
      font_price_number_id: template.font_price_number_id
    });
    setShowTemplateSelector(false);
  };

  // Cargar establecimientos
  useEffect(() => {
    const loadEstablishments = async () => {
      if (!token) {
        setLoadingEstablishments(false);
        return;
      }

      setLoadingEstablishments(true);
      try {
        const params = new URLSearchParams({
          page: '1',
          limit: '1000'
        });

        const route = `establishments/list&${params}`;
        const res = await apiFetch(route, {}, token);
        
        if (isNetworkErrorResponse(res)) {
          setError('Error de conexión al cargar establecimientos');
          return;
        }

        const json = await parseJsonResponse(res);

        if (!res.ok || json.error) {
          const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al cargar establecimientos';
          throw new Error(errorMessage);
        }

        setEstablishments(json.data?.establishments || []);
      } catch (err: any) {
        console.error('Error cargando establecimientos:', err);
        setError(err.message || 'Error al cargar establecimientos');
      } finally {
        setLoadingEstablishments(false);
      }
    };

    loadEstablishments();
  }, [token]);

  // Cargar pantallas cuando se selecciona un establecimiento
  useEffect(() => {
    if (selectedEstablishmentId && token) {
      loadScreens();
      loadAvailableBanners();
      loadAvailableSmartBanners();
      loadAvailableProducts(selectedEstablishmentId);
    } else {
      setScreens([]);
      setAvailableBanners([]);
      setAvailableSmartBanners([]);
      setAvailableReferences([]);
    }
  }, [selectedEstablishmentId, token]);

  const loadScreensForCopy = async () => {
    if (!token || !selectedEstablishmentId) return;
    
    setLoadingScreensForCopy(true);
    try {
      const params = new URLSearchParams({
        establishment_id: selectedEstablishmentId
      });
      const route = `screens/list&${params}`;
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al cargar pantallas');
        return;
      }
      
      const json = await parseJsonResponse(res);
      if (json.error) {
        toast.error(json.error);
        return;
      }
      
      // Filtrar la pantalla actual si está editando
      const allScreens = json.data?.screens || [];
      const filteredScreens = editingScreen 
        ? allScreens.filter((s: Screen) => s.id !== editingScreen.id)
        : allScreens;
      
      setAvailableScreensForCopy(filteredScreens);
    } catch (err: any) {
      console.error('Error cargando pantallas para copiar:', err);
      toast.error('Error al cargar pantallas disponibles');
    } finally {
      setLoadingScreensForCopy(false);
    }
  };

  const handleCopyStyles = (sourceScreen: Screen) => {
    setFormData(prev => ({
      ...prev,
      // Colores
      color_background: sourceScreen.color_background || prev.color_background,
      color_title: sourceScreen.color_title || prev.color_title,
      color_subtitle: sourceScreen.color_subtitle || prev.color_subtitle,
      color_text: sourceScreen.color_text || prev.color_text,
      color_price_text: sourceScreen.color_price_text || prev.color_price_text,
      color_price_number: sourceScreen.color_price_number || prev.color_price_number,
      // Tamaños de fuente
      font_size_title: sourceScreen.font_size_title || prev.font_size_title,
      font_size_subtitle: sourceScreen.font_size_subtitle || prev.font_size_subtitle,
      font_size_text: sourceScreen.font_size_text || prev.font_size_text,
      font_size_price_text: sourceScreen.font_size_price_text || prev.font_size_price_text,
      font_size_price_number: sourceScreen.font_size_price_number || prev.font_size_price_number,
      // IDs de fuentes
      font_title_id: sourceScreen.font_title_id || prev.font_title_id,
      font_subtitle_id: sourceScreen.font_subtitle_id || prev.font_subtitle_id,
      font_text_id: sourceScreen.font_text_id || prev.font_text_id,
      font_script_id: sourceScreen.font_script_id || prev.font_script_id,
      font_price_text_id: sourceScreen.font_price_text_id || prev.font_price_text_id,
      font_price_number_id: sourceScreen.font_price_number_id || prev.font_price_number_id
    }));
    
    setShowCopyStylesModal(false);
    toast.success(`Estilos copiados de "${sourceScreen.name}"`);
  };

  const loadScreens = async (): Promise<Screen[]> => {
    if (!token || !selectedEstablishmentId) return [];
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        establishment_id: selectedEstablishmentId
      });

      const route = `screens/list&${params}`;
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al cargar pantallas');
        return [];
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al cargar pantallas';
        throw new Error(errorMessage);
      }

      const screensList = json.data?.screens || [];
      setScreens(screensList);
      return screensList;
    } catch (err: any) {
      console.error('Error cargando pantallas:', err);
      toast.error(err.message || 'Error al cargar pantallas');
      setScreens([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableBanners = async () => {
    if (!token || !selectedEstablishmentId) return;
    
    setLoadingBanners(true);
    try {
      const params = new URLSearchParams({
        establishment_id: selectedEstablishmentId
      });

      const route = `banners/list&${params}`;
      const res = await apiFetch(route, {}, token);

      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al cargar banners');
        setAvailableBanners([]);
        return;
      }

      const json = await parseJsonResponse(res);

      if (json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al cargar banners';
        toast.error(errorMessage);
        setAvailableBanners([]);
        return;
      }

      const responseData = json as any;
      const banners = responseData.banners || responseData.data?.banners || [];
      // Filtrar solo banners activos (comparar como número para tolerar strings)
      setAvailableBanners(banners.filter((b: Banner) => Number(b.is_active) === 1));
    } catch (err: any) {
      console.error('Error cargando banners:', err);
      toast.error(err.message || 'Error al cargar banners');
      setAvailableBanners([]);
    } finally {
      setLoadingBanners(false);
    }
  };

  const loadAvailableSmartBanners = async () => {
    if (!token || !selectedEstablishmentId) return;
    
    setLoadingSmartBanners(true);
    try {
      const params = new URLSearchParams({
        establishment_id: selectedEstablishmentId
      });

      const route = `smart-banners/list&${params}`;
      const res = await apiFetch(route, {}, token);

      if (isNetworkErrorResponse(res)) {
        setAvailableSmartBanners([]);
        return;
      }

      const json = await parseJsonResponse(res);

      if (json.error) {
        setAvailableSmartBanners([]);
        return;
      }

      const responseData = json.data || json;
      const smartBanners = responseData.smart_banners || [];
      // Filtrar solo smart banners activos
      setAvailableSmartBanners(smartBanners.filter((sb: SmartBanner) => Number(sb.is_active) === 1));
    } catch (err: any) {
      console.error('Error cargando smart banners:', err);
      setAvailableSmartBanners([]);
    } finally {
      setLoadingSmartBanners(false);
    }
  };

  const loadAvailableProducts = async (establishmentIdParam?: string) => {
    if (!token) return;
    
    const targetEstablishmentId = establishmentIdParam || selectedEstablishmentId;
    if (!targetEstablishmentId) return;

    setLoadingProducts(true);
    try {
      const params = new URLSearchParams({
        establishment_id: targetEstablishmentId
      });

      const route = `screens/available-products&${params}`;
      const res = await apiFetch(route, {}, token);

      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al cargar productos disponibles');
        setAvailableReferences([]);
        return;
      }

      const json = await parseJsonResponse(res);

      if (json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al cargar productos disponibles';
        toast.error(errorMessage);
        setAvailableReferences([]);
        return;
      }

      const responseData = (json as any).data || (json as any);
      const references = (responseData?.references || responseData?.products || []).map(
        (reference: ProductReference) => ({
          ...reference,
          variants: reference.variants || []
        })
      );
      setAvailableReferences(references);
    } catch (err: any) {
      console.error('Error cargando productos disponibles:', err);
      toast.error(err.message || 'Error al cargar productos disponibles');
      setAvailableReferences([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadScreenBanners = async (screenId: number, bannerDivisions: number = 2) => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams({
        screen_id: screenId.toString()
      });

      const route = `screens/get-banners&${params}`;
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        return;
      }

      const json = await parseJsonResponse(res);
      
      if (json.error) {
        return;
      }

      // Las respuestas de la API vienen envueltas en data, pero toleramos retorno directo
      const responseData = (json as any).data || (json as any);
      const banners = responseData?.banners || {};
      const lateralesFromApi = banners.laterales?.map((b: any) => b.banner_id) || [];
      
      // Asegurar que el array tenga el tamaño correcto según banner_divisions
      const laterales = [...lateralesFromApi];
      while (laterales.length < bannerDivisions) {
        laterales.push(null);
      }
      while (laterales.length > bannerDivisions) {
        laterales.pop();
      }
      
      // Manejar banners superiores: puede ser array o objeto único
      let superiorBanners: (number | null)[] = [null, null];
      if (Array.isArray(banners.superior)) {
        // Si es array, tomar los primeros 2
        superiorBanners = banners.superior.slice(0, 2).map((b: any) => b.banner_id || null);
        while (superiorBanners.length < 2) {
          superiorBanners.push(null);
        }
      } else if (banners.superior?.banner_id) {
        // Si es objeto único, ponerlo en la primera posición
        superiorBanners = [banners.superior.banner_id, null];
      }
      
      setSelectedBanners({
        superior: superiorBanners,
        laterales: laterales
      });
    } catch (err: any) {
      console.error('Error cargando banners de pantalla:', err);
    }
  };

  const loadScreenComboBanners = async (screenId: number) => {
    if (!token) return;
    
    setLoadingComboBanners(true);
    try {
      const params = new URLSearchParams({
        pantalla_id: screenId.toString()
      });

      const route = `combo-banners/list&${params}`;
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al cargar combo banners');
        return;
      }

      const json = await parseJsonResponse(res);
      
      if (json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al cargar combo banners';
        toast.error(errorMessage);
        return;
      }

      const responseData = (json as any).data || (json as any);
      const comboBannersData = responseData?.combo_banners || [];
      // Normalizar split_position para asegurar que siempre esté definido (null si no existe)
      const normalizedBanners = comboBannersData.map((banner: any) => ({
        ...banner,
        split_position: banner.split_position !== null && banner.split_position !== undefined 
          ? banner.split_position 
          : null
      }));
      console.log('📦 [Screens] Combo banners cargados:', normalizedBanners);
      setComboBanners(normalizedBanners);
    } catch (err: any) {
      console.error('Error cargando combo banners de pantalla:', err);
      toast.error(err.message || 'Error al cargar combo banners');
    } finally {
      setLoadingComboBanners(false);
    }
  };

  const handleDuplicateComboBanner = async (banner: any) => {
    if (!token || !editingScreen) {
      toast.error('Debes estar editando una pantalla para duplicar combo banners');
      return;
    }

    try {
      // Calcular el nuevo display_order (máximo actual + 1)
      const maxDisplayOrder = comboBanners.length > 0 
        ? Math.max(...comboBanners.map(b => b.display_order || 0))
        : 0;
      const newDisplayOrder = maxDisplayOrder + 1;

      // Preparar los datos del banner duplicado
      const duplicatedData = {
        pantalla_id: editingScreen.id,
        producto_id: banner.producto_id,
        title_word1: banner.title_word1,
        title_word1_color: banner.title_word1_color,
        title_word2: banner.title_word2,
        title_word2_color: banner.title_word2_color,
        title_font_id: banner.title_font_id || null,
        title_font_size: banner.title_font_size || 48,
        combo_image_path: banner.combo_image_path,
        product_name: banner.product_name || null,
        product_name_background_color: banner.product_name_background_color || null,
        product_name_font_id: banner.product_name_font_id || null,
        product_name_font_size: banner.product_name_font_size || 24,
        description: banner.description || null,
        description_font_id: banner.description_font_id || null,
        description_font_size: banner.description_font_size || 18,
        description_color: banner.description_color || '#000000',
        legend_text: banner.legend_text || 'Hazlo Gigante por:',
        legend_font_id: banner.legend_font_id || null,
        legend_font_size: banner.legend_font_size || 16,
        legend_text_color: banner.legend_text_color || '#000000',
        legend_price_color: banner.legend_price_color || '#000000',
        price_color: banner.price_color || '#000000',
        price_size: banner.price_size || 24,
        price_font_id: banner.price_font_id || null,
        background_color: banner.background_color || '#ffffff',
        duration: banner.duration || 5,
        display_order: newDisplayOrder,
        split_position: null, // Resetear split_position para que no interfiera
        is_active: 1
      };

      console.log('📋 [Screens] Duplicando combo banner:', duplicatedData);

      const res = await apiFetch(
        'combo-banners/create',
        {
          method: 'POST',
          body: JSON.stringify(duplicatedData)
        },
        token
      );

      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al duplicar combo banner');
        return;
      }

      const json = await parseJsonResponse(res);
      
      if (json.error) {
        toast.error(json.message || 'Error al duplicar combo banner');
        return;
      }

      toast.success('Combo banner duplicado exitosamente');
      
      // Recargar los combo banners para mostrar el nuevo
      if (editingScreen) {
        await loadScreenComboBanners(editingScreen.id);
      }
    } catch (err: any) {
      console.error('Error duplicando combo banner:', err);
      toast.error(err.message || 'Error al duplicar combo banner');
    }
  };

  const loadScreenSmartBanners = async (screenId: number, bannerDivisions: number = 2) => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams({
        screen_id: screenId.toString()
      });

      const route = `screens/get-smart-banners&${params}`;
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        return;
      }

      const json = await parseJsonResponse(res);
      
      if (json.error) {
        return;
      }

      const responseData = (json as any).data || (json as any);
      const smartBanners = responseData?.smart_banners || {};
      
      // Logging para depuración
      console.log('📦 [Screens] Smart banners recibidos:', {
        smartBanners,
        superior: smartBanners.superior,
        laterales: smartBanners.laterales,
        lateralesCount: smartBanners.laterales?.length || 0
      });
      
      // Construir array de laterales preservando la posición lateral
      const laterales: (number | null)[] = [];
      
      // Inicializar array con nulls según banner_divisions
      for (let i = 0; i < bannerDivisions; i++) {
        laterales.push(null);
      }
      
      // Colocar cada banner en su posición lateral correcta usando lateral_position
      if (smartBanners.laterales && Array.isArray(smartBanners.laterales)) {
        smartBanners.laterales.forEach((sb: any) => {
          const lateralPos = sb.lateral_position || 1;
          const arrayIndex = lateralPos - 1; // Convertir 1-based a 0-based (lateral_position 1,2,3 -> array index 0,1,2)
          if (arrayIndex >= 0 && arrayIndex < bannerDivisions) {
            laterales[arrayIndex] = sb.smart_banner_id || sb.id;
          }
        });
      }
      
      setSelectedSmartBanners({
        superior: smartBanners.superior?.smart_banner_id || smartBanners.superior?.id || null,
        laterales: laterales
      });
      
      console.log('📦 [Screens] Smart banners procesados:', {
        superior: smartBanners.superior?.smart_banner_id || smartBanners.superior?.id || null,
        laterales: laterales
      });
    } catch (err: any) {
      console.error('Error cargando smart banners de pantalla:', err);
    }
  };

  const loadScreenProducts = async (screenId: number, productsLimit: number | null = null) => {
    if (!token) return;

    try {
      const params = new URLSearchParams({
        screen_id: screenId.toString()
      });

      const route = `screens/get-products&${params}`;
      const res = await apiFetch(route, {}, token);

      if (isNetworkErrorResponse(res)) {
        return;
      }

      const json = await parseJsonResponse(res);

      if (json.error) {
        return;
      }

      const responseData = (json as any).data || (json as any);
      let references = (responseData?.references || responseData?.products || []).map(
        (reference: ProductReference) => ({
          ...reference,
          variants: reference.variants || []
        })
      ) as ProductReference[];

      if (productsLimit !== null && references.length > productsLimit) {
        references = references.slice(0, productsLimit);
      }

      setSelectedReferences(references);
    } catch (err: any) {
      console.error('Error cargando productos de la pantalla:', err);
    }
  };

  const handleEdit = async (screen: Screen) => {
    setEditingScreen(screen);
    setFormData({
      name: screen.name,
      orientation: screen.orientation,
      content_type: screen.content_type,
      slider_type: (screen as any).slider_type || 'fade',
      banner_side: screen.banner_side || 'left',
      banner_divisions: screen.banner_divisions,
      products_count: screen.products_count,
      color_background: screen.color_background || '#ffffff',
      color_title: screen.color_title || '#000000',
      color_subtitle: screen.color_subtitle || '#333333',
      color_text: screen.color_text || '#666666',
      color_price_text: screen.color_price_text || '#333333',
      color_price_number: screen.color_price_number || '#333333',
      font_size_title: screen.font_size_title || 24,
      font_size_subtitle: screen.font_size_subtitle || 18,
      font_size_text: screen.font_size_text || 14,
      font_size_price_text: screen.font_size_price_text || 16,
      font_size_price_number: screen.font_size_price_number || 16,
      font_title_id: screen.font_title_id || null,
      font_subtitle_id: screen.font_subtitle_id || null,
      font_text_id: screen.font_text_id || null,
      font_script_id: screen.font_script_id || null,
      font_price_text_id: screen.font_price_text_id || null,
      font_price_number_id: screen.font_price_number_id || null
    });
    
    // Cargar banners de la pantalla
    if (screen.content_type === 'menu_and_banners' || screen.content_type === 'banners_only') {
      await loadScreenBanners(screen.id, screen.banner_divisions);
      // Cargar smart banners de la pantalla
      await loadScreenSmartBanners(screen.id, screen.banner_divisions);
    } else {
      setSelectedBanners({
        superior: [null, null],
        laterales: []
      });
      // Limpiar smart banners también
      setSelectedSmartBanners({
        superior: null,
        laterales: []
      });
    }

    // Cargar combo banners si es banners_only
    if (screen.content_type === 'banners_only') {
      await loadScreenComboBanners(screen.id);
    } else {
      setComboBanners([]);
    }

    // Cargar productos de la pantalla
    if (screen.content_type === 'menu_and_banners' || screen.content_type === 'menu_only') {
      const productsLimit = screen.content_type === 'menu_and_banners' ? screen.products_count : null;
      await loadScreenProducts(screen.id, productsLimit);
    } else {
      setSelectedReferences([]);
    }
    
    setActiveTab('config');
    setShowForm(true);
  };

  const handleDelete = async (screenId: number) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta pantalla? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const response = await apiFetch(`screens/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id: screenId })
      });

      const json = await parseJsonResponse(response);

      if (!response.ok) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al eliminar la pantalla';
        throw new Error(errorMessage);
      }

      toast.success('Pantalla eliminada exitosamente');
      // Recargar la lista de pantallas
      if (selectedEstablishmentId) {
        loadScreens();
      }
    } catch (error) {
      console.error('Error al eliminar pantalla:', error);
      toast.error(error instanceof Error ? error.message : 'Error al eliminar la pantalla');
    }
  };

  const resetForm = () => {
    setEditingScreen(null);
    setSelectedTemplate(null);
    setShowTemplateSelector(false);
    setFormData({
      name: '',
      orientation: 'horizontal',
      content_type: 'menu_and_banners',
      slider_type: 'fade',
      banner_side: 'left',
      banner_divisions: 2, // Valor por defecto, se ajustará según plantilla
      products_count: 6,
      color_background: '#ffffff',
      color_title: '#000000',
      color_subtitle: '#333333',
      color_text: '#666666',
      color_price_text: '#333333',
      color_price_number: '#333333',
      font_size_title: 24,
      font_size_subtitle: 18,
      font_size_text: 14,
      font_size_price_text: 16,
      font_size_price_number: 16,
      font_title_id: null,
      font_subtitle_id: null,
      font_text_id: null,
      font_script_id: null,
      font_price_text_id: null,
      font_price_number_id: null
    });
    setSelectedBanners({
      superior: [null, null],
      laterales: []
    });
    setSelectedReferences([]);
    setComboBanners([]);
    setProductSearch('');
    setActiveTab('config');
    setShowComboBannerForm(false);
    setEditingComboBanner(null);
  };

  useEffect(() => {
    if (formData.content_type === 'banners_only') {
      if (selectedReferences.length > 0) {
        setSelectedReferences([]);
      }
      return;
    } else {
      // Si cambió de banners_only a otro tipo, limpiar combo banners
      if (comboBanners.length > 0) {
        setComboBanners([]);
      }
    }

    if (formData.content_type === 'menu_and_banners') {
      const limit = formData.products_count;
      if (selectedReferences.length > limit) {
        setSelectedReferences((prev) => prev.slice(0, limit));
        toast.warning(`Se ajustó la lista de productos al nuevo límite de ${limit}.`);
      }
    }
  }, [formData.content_type, formData.products_count]);

  // Ajustar banner_divisions según products_count
  useEffect(() => {
    if (formData.products_count === 6) {
      // Con 6 productos, permitir 0, 2, 3 o 4 divisiones
      // 0 = sin banners laterales, 2 = banners normales, 3 o 4 = smart banners
      if (formData.banner_divisions !== 0 && formData.banner_divisions !== 2 && formData.banner_divisions !== 3 && formData.banner_divisions !== 4) {
        setFormData(prev => ({ ...prev, banner_divisions: 2 }));
      }
    } else if (formData.products_count === 10) {
      // Con 10 productos, solo 2 divisiones (banners normales)
      if (formData.banner_divisions !== 2) {
        setFormData(prev => ({ ...prev, banner_divisions: 2 }));
      }
    }
  }, [formData.products_count]);

  const formatPrice = (price: string | number) => {
    const numericPrice = typeof price === 'number' ? price : parseFloat(price);
    if (Number.isNaN(numericPrice)) {
      return '$0';
    }
    return Math.round(numericPrice).toLocaleString('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const handleAddProduct = (reference: ProductReference) => {
    if (!reference.variants || reference.variants.length === 0) {
      toast.error('Este producto no tiene variantes disponibles para mostrar.');
      return;
    }

    if (selectedReferences.find((p) => p.reference_code === reference.reference_code)) {
      return;
    }

    if (formData.content_type === 'menu_and_banners' && selectedReferences.length >= formData.products_count) {
      toast.warning(`Solo puedes seleccionar ${formData.products_count} productos para esta pantalla.`);
      return;
    }

    setSelectedReferences((prev) => [
      ...prev,
      {
        ...reference,
        variants: [...(reference.variants || [])]
      }
    ]);
  };

  const handleRemoveProduct = (referenceCode: string) => {
    setSelectedReferences((prev) => prev.filter((product) => product.reference_code !== referenceCode));
  };

  const moveProduct = (index: number, direction: 'up' | 'down') => {
    setSelectedReferences((prev) => {
      const newProducts = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;

      if (targetIndex < 0 || targetIndex >= newProducts.length) {
        return prev;
      }

      [newProducts[index], newProducts[targetIndex]] = [newProducts[targetIndex], newProducts[index]];
      return newProducts;
    });
  };

  const handleSubmit = async () => {
    if (!token || !selectedEstablishmentId) {
      toast.error('Debes seleccionar un establecimiento');
      return;
    }

    // Prevenir múltiples envíos
    if (submitting) {
      return;
    }

    if (!formData.name.trim()) {
      toast.error('El nombre de la pantalla es requerido');
      return;
    }

    // Validar campos según content_type
    if (formData.content_type === 'menu_and_banners') {
      // En pantallas verticales, banner_side debe ser NULL (no se valida)
      if (formData.orientation === 'horizontal' && !formData.banner_side) {
        toast.error('Debes seleccionar el lado de los banners');
        return;
      }
    }

    if ((formData.content_type === 'menu_and_banners' || formData.content_type === 'menu_only') && selectedReferences.length === 0) {
      toast.error('Debes seleccionar al menos un producto para la pantalla');
      return;
    }

    if (formData.content_type === 'menu_and_banners' && selectedReferences.length > formData.products_count) {
      toast.error(`Solo puedes seleccionar ${formData.products_count} productos para esta pantalla`);
      return;
    }

    setSubmitting(true);
    try {
      // Construir array de banners
      const banners: SelectedBanner[] = [];
      
      // Incluir banners normales según products_count
      if (formData.content_type === 'menu_and_banners' || formData.content_type === 'banners_only') {
        // Banners superiores - múltiples para verticales, uno para horizontales
        if (formData.orientation === 'vertical') {
          // Para verticales: hasta 2 banners superiores
          selectedBanners.superior.forEach((bannerId, index) => {
            if (bannerId) {
              banners.push({
                banner_id: bannerId,
                position: 'superior',
                display_order: index
              });
            }
          });
        } else {
          // Para horizontales: solo un banner superior
          if (selectedBanners.superior[0]) {
            banners.push({
              banner_id: selectedBanners.superior[0],
              position: 'superior',
              display_order: 0
            });
          }
        }
        
        // Banners laterales - Permitir si:
        // - products_count es 10, O
        // - products_count es 6 Y banner_divisions es 2
        if (formData.products_count === 10 || 
            (formData.products_count === 6 && formData.banner_divisions === 2)) {
        selectedBanners.laterales.forEach((bannerId, index) => {
          if (bannerId) {
            banners.push({
              banner_id: bannerId,
              position: 'lateral',
              lateral_position: index + 1,
              display_order: index
            });
          }
        });
        }
      }

      // Construir array de smart banners
      const smartBanners: SelectedSmartBanner[] = [];
      
      // SOLO incluir smart banners si products_count es 6 Y banner_divisions es 3 o 4
      if ((formData.content_type === 'menu_and_banners' || formData.content_type === 'banners_only') 
          && formData.products_count === 6 
          && (formData.banner_divisions === 3 || formData.banner_divisions === 4)) {
        // Smart Banner superior
        if (selectedSmartBanners.superior) {
          smartBanners.push({
            smart_banner_id: selectedSmartBanners.superior,
            position: 'superior',
            display_order: 0
          });
        }
        
        // Smart Banners laterales - usar índice del array directamente para lateral_position
        selectedSmartBanners.laterales.forEach((smartBannerId, index) => {
          if (smartBannerId) {
            smartBanners.push({
              smart_banner_id: smartBannerId,
              position: 'lateral',
              lateral_position: index + 1, // Índice del array (0-based) + 1 = posición lateral (1, 2, 3)
              display_order: index
            });
          }
        });
      }

      // Log para debugging
      console.log('🔍 DEBUG - Banners a enviar:', banners);
      console.log('🔍 DEBUG - selectedBanners state:', selectedBanners);
      console.log('🔍 DEBUG - content_type:', formData.content_type);
      console.log('🔍 DEBUG - banners array length:', banners.length);

      const payload: any = {
        name: formData.name.trim(),
        orientation: formData.orientation,
        content_type: formData.content_type,
        slider_type: formData.content_type === 'advertising_only' ? formData.slider_type : undefined,
        template_id: selectedTemplate?.id || 1,
        banner_side: formData.content_type === 'menu_and_banners' ? formData.banner_side : null,
        banner_divisions: formData.content_type === 'menu_and_banners' ? formData.banner_divisions : 2,
        products_count: formData.content_type === 'menu_and_banners' ? formData.products_count : 6,
        color_background: formData.color_background,
        color_title: formData.color_title,
        color_subtitle: formData.color_subtitle,
        color_text: formData.color_text,
        color_price_text: formData.color_price_text,
        color_price_number: formData.color_price_number,
        font_size_title: formData.font_size_title,
        font_size_subtitle: formData.font_size_subtitle,
        font_size_text: formData.font_size_text,
        font_size_price_text: formData.font_size_price_text,
        font_size_price_number: formData.font_size_price_number,
        font_title_id: formData.font_title_id,
        font_subtitle_id: formData.font_subtitle_id,
        font_text_id: formData.font_text_id,
        font_script_id: formData.font_script_id,
        font_price_text_id: formData.font_price_text_id,
        font_price_number_id: formData.font_price_number_id,
        banners: banners,
        smart_banners: smartBanners
      };

      if (formData.content_type === 'menu_and_banners' || formData.content_type === 'menu_only') {
        payload.products = selectedReferences.map((reference, index) => ({
          reference_code: reference.reference_code,
          display_order: index,
          variants: (reference.variants || []).map((variant, variantIndex) => ({
            product_id: variant.product_id,
            variant_order: variantIndex
          }))
        }));
      }

      // Agregar combo banners si es banners_only
      // IMPORTANTE: Solo enviar combo_banners si hay banners en el estado
      // Si no hay banners, el backend mantendrá los existentes
      if (formData.content_type === 'banners_only') {
        console.log('💾 [Screens] Guardando combo banners:', comboBanners);
        if (comboBanners.length > 0) {
          payload.combo_banners = comboBanners.map((banner, index) => ({
            id: banner.id || undefined, // Incluir ID si existe para actualizar, undefined para crear nuevo
            producto_id: banner.producto_id,
            title_word1: banner.title_word1,
            title_word1_color: banner.title_word1_color,
            title_word2: banner.title_word2,
            title_word2_color: banner.title_word2_color,
            title_font_id: banner.title_font_id || null,
            title_font_size: banner.title_font_size || 48,
            combo_image_path: banner.combo_image_path,
            product_name: banner.product_name || null,
            product_name_background_color: banner.product_name_background_color || null,
            product_name_font_id: banner.product_name_font_id || null,
            product_name_font_size: banner.product_name_font_size || 24,
            description: banner.description || null,
            description_font_id: banner.description_font_id || null,
            description_font_size: banner.description_font_size || 18,
            description_color: banner.description_color || '#000000',
            legend_text: banner.legend_text,
            legend_font_id: banner.legend_font_id || null,
            legend_font_size: banner.legend_font_size || 16,
            legend_text_color: banner.legend_text_color || '#000000',
            legend_price_color: banner.legend_price_color || '#000000',
            price_color: banner.price_color,
            price_size: banner.price_size,
            price_font_id: banner.price_font_id || null,
            background_color: banner.background_color,
            duration: banner.duration,
            display_order: banner.display_order || index,
            split_position: banner.split_position !== null && banner.split_position !== undefined 
              ? banner.split_position 
              : null,
            is_active: banner.is_active ?? 1
          }));
          console.log('📤 [Screens] Payload combo_banners enviado:', payload.combo_banners);
        } else {
          // Si no hay combo banners en el estado, no enviar el campo
          // El backend mantendrá los existentes
          console.log('⚠️ [Screens] No hay combo banners en el estado, no se enviará combo_banners en el payload');
        }
      }

      const route = editingScreen ? 'screens/update' : 'screens/create';
      
      if (editingScreen) {
        payload.id = editingScreen.id;
      } else {
        payload.establishment_id = parseInt(selectedEstablishmentId, 10);
      }

      const res = await apiFetch(
        route,
        {
          method: 'POST',
          body: JSON.stringify(payload)
        },
        token
      );

      if (isNetworkErrorResponse(res)) {
        toast.error(`Error de conexión al ${editingScreen ? 'actualizar' : 'crear'} pantalla`);
        return;
      }

      const json = await parseJsonResponse(res);

      if (json.error || !json.success) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || `Error al ${editingScreen ? 'actualizar' : 'crear'} la pantalla`;
        toast.error(errorMessage);
        return;
      }

      toast.success(`Pantalla ${editingScreen ? 'actualizada' : 'creada'} exitosamente`);
      
      // Recargar lista de pantallas
      await loadScreens();
      
      // Si se está editando, recargar los datos de la pantalla editada
      if (editingScreen) {
        // SOLUCIÓN: Llamada directa a la API para obtener la pantalla actualizada
        try {
          const screenRes = await apiFetch(`screens/get&id=${editingScreen.id}`, {}, token);
          if (!isNetworkErrorResponse(screenRes)) {
            const screenJson = await parseJsonResponse(screenRes);
            if (screenJson.data?.screen) {
              // Recargar los datos de la pantalla directamente
              await handleEdit(screenJson.data.screen);
            } else {
              // Fallback: buscar en la lista recargada
              const updatedScreens = await loadScreens();
              const updatedScreen = updatedScreens.find(s => s.id === editingScreen.id);
              if (updatedScreen) {
                await handleEdit(updatedScreen);
              }
            }
          } else {
            // Fallback si hay error de red
            const updatedScreens = await loadScreens();
            const updatedScreen = updatedScreens.find(s => s.id === editingScreen.id);
            if (updatedScreen) {
              await handleEdit(updatedScreen);
            }
          }
        } catch (err) {
          console.error('Error obteniendo pantalla actualizada:', err);
          // Intentar fallback con la lista
          const updatedScreens = await loadScreens();
          const updatedScreen = updatedScreens.find(s => s.id === editingScreen.id);
          if (updatedScreen) {
            await handleEdit(updatedScreen);
          }
        }
        // No cerrar el formulario, mantenerlo abierto con los datos actualizados
        return;
      }
      
      setShowForm(false);
      resetForm();
    } catch (err: any) {
      console.error(`Error ${editingScreen ? 'actualizando' : 'creando'} pantalla:`, err);
      toast.error(err.message || `Error al ${editingScreen ? 'actualizar' : 'crear'} la pantalla`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleProductsCountChange = (newProductsCount: number) => {
    const currentProductsCount = formData.products_count;
    
    // Si no hay cambio, no hacer nada
    if (currentProductsCount === newProductsCount) {
      return;
    }
    
    // Si hay un template seleccionado, respetar su banner_divisions
    // Solo calcular automáticamente si no hay template o si el usuario está editando manualmente
    let newBannerDivisions: number;
    
    if (selectedTemplate && newProductsCount === 6) {
      // Si hay template seleccionado y es 6 productos, usar el banner_divisions del template
      newBannerDivisions = selectedTemplate.banner_divisions;
    } else if (newProductsCount === 10) {
      // Para 10 productos, siempre es 2
      newBannerDivisions = 2;
    } else {
      // Por defecto para 6 productos sin template: 2 (Diseño Clásico)
      newBannerDivisions = 2;
    }
    
    // SIEMPRE mostrar confirmación cuando cambia products_count
    const message = currentProductsCount === 6 && newProductsCount === 10
      ? 'Si cambias a 10 productos, los banners inteligentes se desactivarán y solo se mostrarán banners normales. Deberás configurarlos nuevamente si vuelves a 6 productos.'
      : currentProductsCount === 10 && newProductsCount === 6
      ? `Si cambias a 6 productos, se aplicará la configuración de banners del diseño seleccionado (${newBannerDivisions === 0 ? 'sin banners laterales' : newBannerDivisions === 2 ? '2 banners normales laterales' : newBannerDivisions === 3 ? '3 banners inteligentes laterales' : '4 banners inteligentes laterales'}). Deberás verificar y reconfigurar los banners si es necesario.`
      : 'Si cambias la cantidad de productos, deberás verificar y reconfigurar los banners.';
    
    toast.warning('¿Cambiar cantidad de productos?', {
      description: message,
      duration: 10000, // 10 segundos para dar tiempo a leer
      action: {
        label: 'Sí, cambiar',
        onClick: () => {
          // Ajustar arrays de laterales según nuevo banner_divisions
          const currentLaterales = [...selectedBanners.laterales];
          while (currentLaterales.length < newBannerDivisions) {
            currentLaterales.push(null);
          }
          while (currentLaterales.length > newBannerDivisions) {
            currentLaterales.pop();
          }
          
          const currentSmartBannerLaterales = [...selectedSmartBanners.laterales];
          while (currentSmartBannerLaterales.length < newBannerDivisions) {
            currentSmartBannerLaterales.push(null);
          }
          while (currentSmartBannerLaterales.length > newBannerDivisions) {
            currentSmartBannerLaterales.pop();
          }
          
          setFormData({ 
            ...formData, 
            products_count: newProductsCount,
            banner_divisions: newBannerDivisions
          });
          
          setSelectedBanners({
            ...selectedBanners,
            laterales: currentLaterales
          });
          
          setSelectedSmartBanners({
            ...selectedSmartBanners,
            laterales: currentSmartBannerLaterales
          });
          
          // Si se cambia a 10 productos, limpiar smart banners seleccionados
          if (newProductsCount === 10) {
            setSelectedSmartBanners({
              superior: null,
              laterales: []
            });
          }
          
          // Si se cambia a 6 productos, limpiar banners según el banner_divisions del template
          if (newProductsCount === 6) {
            if (newBannerDivisions === 0) {
              // Diseño Amplio: sin banners laterales, mantener solo superior
              setSelectedBanners({
                ...selectedBanners,
                laterales: []
              });
              setSelectedSmartBanners({
                ...selectedSmartBanners,
                laterales: []
              });
            } else if (newBannerDivisions === 2) {
              // Diseño Clásico: banners normales laterales, limpiar smart banners
              setSelectedSmartBanners({
                superior: null,
                laterales: []
              });
            } else if (newBannerDivisions === 3 || newBannerDivisions === 4) {
              // Diseño Inteligente: smart banners laterales, limpiar banners normales laterales
              setSelectedBanners({
                ...selectedBanners,
                laterales: []
              });
            }
          }
        },
      },
      cancel: {
        label: 'Cancelar',
        onClick: () => {
          // No hacer nada, solo cerrar el toast
        },
      },
    });
  };

  // Función para forzar refresh de la pantalla
  const handleForceRefresh = async () => {
    if (!editingScreen || !token) {
      toast.error('No hay pantalla seleccionada para refrescar');
      return;
    }
    
    try {
      const res = await apiFetch('screens/force-refresh', {
        method: 'POST',
        body: JSON.stringify({ id: editingScreen.id })
      }, token);
      
      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al forzar refresh');
        return;
      }
      
      const json = await parseJsonResponse(res);
      
      if (!res.ok || json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al forzar refresh';
        throw new Error(errorMessage);
      }
      
      toast.success('Refresh forzado exitosamente. La pantalla se actualizará en la próxima recarga.');
    } catch (err: any) {
      toast.error(err.message || 'Error al forzar refresh');
    }
  };

  const getContentTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'menu_only': 'Solo Menú',
      'banners_only': 'Solo Banners',
      'menu_and_banners': 'Menú y Banners',
      'advertising_only': 'Solo Publicitario'
    };
    return labels[type] || type;
  };

  const getOrientationLabel = (orientation: string): string => {
    return orientation === 'horizontal' ? 'Horizontal' : 'Vertical';
  };

  // Función helper para formatear fechas
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const isMenuContent = formData.content_type === 'menu_and_banners' || formData.content_type === 'menu_only';
  const bannersEnabled = formData.content_type === 'menu_and_banners';
  const productsLimit = formData.content_type === 'menu_and_banners' ? formData.products_count : null;
  const tabOptions: Array<{ id: 'config' | 'banners' | 'products' | 'styles' | 'slider-images'; label: string }> = [
    { id: 'config', label: 'Configuración' },
    { id: 'banners', label: 'Banners' },
    { id: 'products', label: 'Productos' },
    ...(canView('button.screen_styles_tab') ? [{ id: 'styles' as const, label: 'Estilos' }] : []),
    ...(formData.content_type === 'advertising_only' ? [{ id: 'slider-images' as const, label: 'Imágenes' }] : [])
  ];
  const normalizedSearch = productSearch.trim().toLowerCase();
  const filteredAvailableProducts = availableReferences.filter((product) => {
    if (!normalizedSearch) return true;
    const nameMatch = product.base_name.toLowerCase().includes(normalizedSearch);
    const familyMatch = (product.family || '').toLowerCase().includes(normalizedSearch);
    const subcategoryMatch = (product.subcategoria || '').toLowerCase().includes(normalizedSearch);
    const variantMatch = product.variants.some(
      (variant) =>
        variant.variant_label.toLowerCase().includes(normalizedSearch) ||
        variant.name?.toLowerCase().includes(normalizedSearch)
    );
    return nameMatch || familyMatch || subcategoryMatch || variantMatch;
  });

  return (
    <DashboardLayout
      title="Gestión de Pantallas"
      subtitle="Configura y gestiona las pantallas de visualización"
      showBackButton={true}
      backPath="/management"
    >
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Selector de establecimiento */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Select
                    label="Establecimiento"
                    value={selectedEstablishmentId}
                    onChange={(e) => {
                      setSelectedEstablishmentId(e.target.value);
                      setScreens([]);
                    }}
                    options={[
                      { value: '', label: '-- Selecciona un establecimiento --' },
                      ...establishments.filter(est => est.is_active === 1).map((est) => ({
                        value: est.id.toString(),
                        label: `${est.name_establishment} (Cód: ${est.code_establishment_hioffice})`
                      }))
                    ]}
                    disabled={loadingEstablishments}
                  />
                </div>
                {selectedEstablishmentId && can('screens.create') && (
                  <div className="flex items-end">
                    <Button
                      variant="primary"
                      size="md"
                      onClick={async () => {
                        resetForm();
                        await loadTemplates();
                        setShowTemplateSelector(true);
                        setShowForm(true);
                      }}
                    >
                      + Nueva Pantalla
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Mensaje de error */}
          {error && (
            <Alert variant="error" className="mb-6">
              {error}
            </Alert>
          )}

          {/* Lista de pantallas */}
          {loading ? (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-gray-500">Cargando pantallas...</p>
              </CardContent>
            </Card>
          ) : screens.length === 0 && selectedEstablishmentId ? (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-gray-500">No se encontraron pantallas para este establecimiento.</p>
                <Button
                  variant="primary"
                  size="md"
                  className="mt-4"
                  onClick={async () => {
                    resetForm();
                    await loadTemplates();
                    setShowTemplateSelector(true);
                    setShowForm(true);
                  }}
                >
                  Crear Primera Pantalla
                </Button>
              </CardContent>
            </Card>
          ) : screens.length > 0 ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">
                  Pantallas ({screens.length})
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {screens.map((screen) => (
                  <Card key={screen.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-800 mb-1">
                            {screen.name}
                          </h3>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Badge variant="info">
                              {getOrientationLabel(screen.orientation)}
                            </Badge>
                            <Badge variant={screen.content_type === 'menu_and_banners' ? 'success' : 'neutral'}>
                              {getContentTypeLabel(screen.content_type)}
                            </Badge>
                            {screen.is_active === 1 ? (
                              <Badge variant="success">Activa</Badge>
                            ) : (
                              <Badge variant="error">Inactiva</Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        {screen.content_type === 'menu_and_banners' && (
                          <>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Lado banners:</span>
                              <span className="text-gray-800 font-medium">
                                {screen.banner_side === 'left' ? 'Izquierda' : 'Derecha'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">División banners:</span>
                              <span className="text-gray-800 font-medium">{screen.banner_divisions}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Productos:</span>
                              <span className="text-gray-800 font-medium">{screen.products_count}</span>
                            </div>
                          </>
                        )}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Accesos:</span>
                          <span className="text-gray-800 font-medium">{screen.access_count}</span>
                        </div>
                      </div>

                      {/* Fechas */}
                      <div className="space-y-1 text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">
                        <div className="flex justify-between">
                          <span>Creado:</span>
                          <span>{formatDate(screen.created_at)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Actualizado:</span>
                          <span>{formatDate(screen.updated_at)}</span>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                          <span>Token: {screen.url_token.substring(0, 8)}...</span>
                          {screen.last_accessed && (
                            <span>
                              {new Date(screen.last_accessed).toLocaleDateString('es-MX')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              const url = `/display/${screen.url_token}`;
                              window.open(url, '_blank');
                            }}
                            className="flex-1 flex items-center justify-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span>Abrir Pantalla</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(screen)}
                            className="px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors"
                            title="Editar pantalla"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </Button>
                          {can('screens.delete') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(screen.id)}
                              className="px-3 text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                              title="Eliminar pantalla"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-gray-500">Selecciona un establecimiento para ver las pantallas.</p>
              </CardContent>
            </Card>
          )}

          {/* Selector de Plantillas */}
          {showTemplateSelector && !editingScreen && (
            <Sheet
              isOpen={showForm && showTemplateSelector}
              onClose={() => {
                setShowForm(false);
                setShowTemplateSelector(false);
                resetForm();
              }}
              title="Selecciona un diseño para tu pantalla"
              size="lg"
            >
              <div className="space-y-4">
                {loadingTemplates ? (
                  <div className="text-center py-8 text-gray-500">Cargando plantillas...</div>
                ) : templates.length === 0 ? (
                  <Alert variant="warning">
                    No hay plantillas disponibles. Por favor, contacta al administrador.
                  </Alert>
                ) : (
                  templates.map((template) => (
                    <Card
                      key={template.id}
                      className="cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-xl mb-2">{template.name}</h3>
                        {template.description && (
                          <p className="text-sm text-gray-600 mb-4">{template.description}</p>
                        )}
                        <div className="flex flex-wrap gap-2 text-xs">
                          <Badge variant="neutral">
                            {template.orientation === 'horizontal' ? 'Horizontal' : 'Vertical'}
                          </Badge>
                          <Badge variant="neutral">
                            {template.products_count} productos
                          </Badge>
                          <Badge variant="neutral">
                            {template.banner_divisions} banners laterales
                          </Badge>
                          {template.banner_side && (
                            <Badge variant="neutral">
                              Banners {template.banner_side === 'left' ? 'izquierda' : 'derecha'}
                            </Badge>
                          )}
                          <Badge variant="neutral">
                            {template.content_type === 'menu_only' ? 'Solo Menú' :
                             template.content_type === 'banners_only' ? 'Solo Banners' :
                             template.content_type === 'advertising_only' ? 'Solo Publicitario' :
                             'Menú y Banners'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </Sheet>
          )}

          {/* Formulario de creación/edición */}
          <Sheet
            isOpen={showForm && !showTemplateSelector}
            onClose={() => {
              setShowForm(false);
              setShowTemplateSelector(false);
              resetForm();
            }}
            title={editingScreen ? 'Editar Pantalla' : 'Nueva Pantalla'}
            size="xl"
          >
            <div className="space-y-6">
              <div className="flex flex-wrap gap-2 border-b border-gray-200">
                {tabOptions.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors ${
                        isActive
                          ? 'text-red-600 border-red-600 bg-white'
                          : 'text-gray-500 border-transparent hover:text-gray-700'
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {activeTab === 'config' && (
                <div className="space-y-4">
                  <Input
                    label="Nombre de la Pantalla"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Pantalla Principal"
                    required
                  />

                  <Select
                    label="Orientación"
                    value={formData.orientation}
                    onChange={(e) => {
                      const newOrientation = e.target.value as 'horizontal' | 'vertical';
                      const updates: any = { orientation: newOrientation };
                      
                      if (newOrientation === 'vertical') {
                        // En verticales, forzar valores específicos
                        updates.products_count = 6;
                        updates.banner_side = null;
                        updates.banner_divisions = 0;
                      }
                      
                      setFormData({ ...formData, ...updates });
                    }}
                    options={[
                      { value: 'horizontal', label: 'Horizontal' },
                      { value: 'vertical', label: 'Vertical' }
                    ]}
                  />
                  
                  {formData.orientation === 'vertical' && (
                    <Alert variant="info" className="mb-4">
                      Las pantallas verticales muestran 6 productos arriba y banners inteligentes abajo. Los banners laterales no están disponibles en esta orientación.
                    </Alert>
                  )}

                  <Select
                    label="Función de la Pantalla"
                    value={formData.content_type}
                    onChange={(e) => {
                      const newContentType = e.target.value as 'menu_only' | 'banners_only' | 'menu_and_banners' | 'advertising_only';
                      setFormData({
                        ...formData,
                        content_type: newContentType,
                        banner_side: newContentType === 'menu_and_banners' ? 'left' : null,
                        slider_type: newContentType === 'advertising_only' ? 'fade' : ('fade' as 'fade' | 'slide' | 'zoom')
                      });
                      
                      if (newContentType === 'menu_only') {
                        setSelectedBanners({
                          superior: [null, null],
                          laterales: []
                        });
                        setSelectedSmartBanners({
                          superior: null,
                          laterales: []
                        });
                      } else if (newContentType === 'banners_only') {
                        setSelectedBanners({
                          ...selectedBanners,
                          laterales: []
                        });
                        setSelectedSmartBanners({
                          ...selectedSmartBanners,
                          laterales: []
                        });
                      } else if (newContentType === 'advertising_only') {
                        setSelectedBanners({
                          superior: [null, null],
                          laterales: []
                        });
                        setSelectedSmartBanners({
                          superior: null,
                          laterales: []
                        });
                        setSelectedReferences([]);
                      }
                    }}
                    options={[
                      { value: 'menu_only', label: 'Solo Menú' },
                      { value: 'banners_only', label: 'Solo Banners' },
                      { value: 'menu_and_banners', label: 'Menú y Banners' },
                      { value: 'advertising_only', label: 'Solo Publicitario' }
                    ]}
                  />

                  {formData.content_type === 'advertising_only' && (
                    <Select
                      label="Tipo de Animación del Slider"
                      value={formData.slider_type || 'fade'}
                      onChange={(e) => setFormData({ ...formData, slider_type: e.target.value as 'fade' | 'slide' | 'zoom' })}
                      options={[
                        { value: 'fade', label: 'Fade (Desvanecimiento)' },
                        { value: 'slide', label: 'Slide (Deslizamiento)' },
                        { value: 'zoom', label: 'Zoom (Acercamiento)' }
                      ]}
                    />
                  )}

                  {formData.content_type === 'menu_and_banners' && (
                    <>
                      {formData.orientation === 'vertical' ? (
                        <Input
                          label="Lado de Banners"
                          type="text"
                          value="No aplica (vertical)"
                          disabled
                        />
                      ) : (
                        <Select
                          label="Lado de Banners"
                          value={formData.banner_side || 'left'}
                          onChange={(e) => setFormData({ ...formData, banner_side: e.target.value as 'left' | 'right' })}
                          options={[
                            { value: 'left', label: 'Izquierda' },
                            { value: 'right', label: 'Derecha' }
                          ]}
                        />
                      )}

                      {formData.orientation === 'vertical' ? (
                        <Input
                          label="Cantidad de Productos"
                          type="text"
                          value="6 (fijo para verticales)"
                          disabled
                        />
                      ) : (
                        <Select
                          label="Cantidad de Productos"
                          value={formData.products_count.toString()}
                          onChange={(e) => {
                            const newProductsCount = parseInt(e.target.value, 10);
                            handleProductsCountChange(newProductsCount);
                          }}
                          options={[
                            { value: '6', label: '6 productos' },
                            { value: '10', label: '10 productos' }
                          ]}
                        />
                      )}

                      {formData.orientation === 'vertical' ? (
                        <Input
                          label="División de Banners"
                          type="text"
                          value="No aplica (vertical)"
                          disabled
                        />
                      ) : (
                        <Select
                          label="División de Banners"
                          value={formData.banner_divisions.toString()}
                          disabled={formData.products_count === 10}
                          onChange={(e) => {
                            const newDivisions = parseInt(e.target.value, 10);
                            setFormData({ ...formData, banner_divisions: newDivisions });
                            
                            // Ajustar arrays de laterales según nuevo banner_divisions
                            const currentLaterales = [...selectedBanners.laterales];
                            while (currentLaterales.length < newDivisions) {
                              currentLaterales.push(null);
                            }
                            while (currentLaterales.length > newDivisions) {
                              currentLaterales.pop();
                            }
                            
                            const currentSmartBannerLaterales = [...selectedSmartBanners.laterales];
                            while (currentSmartBannerLaterales.length < newDivisions) {
                              currentSmartBannerLaterales.push(null);
                            }
                            while (currentSmartBannerLaterales.length > newDivisions) {
                              currentSmartBannerLaterales.pop();
                            }
                            
                            setSelectedBanners({
                              ...selectedBanners,
                              laterales: currentLaterales
                            });
                            
                            setSelectedSmartBanners({
                              ...selectedSmartBanners,
                              laterales: currentSmartBannerLaterales
                            });
                          }}
                          options={[
                            { value: '0', label: 'Sin banners laterales' },
                            { value: '2', label: '2 espacios' },
                            { value: '3', label: '3 espacios' },
                            { value: '4', label: '4 espacios' }
                          ]}
                          helperText={
                            formData.products_count === 10
                              ? 'Este dato no es modificable. Se establece automáticamente en 2 espacios para 10 productos.'
                              : formData.banner_divisions === 0
                              ? 'Sin banners laterales. Los productos ocuparán todo el ancho de la pantalla.'
                              : formData.banner_divisions === 2
                              ? '2 espacios para banners normales laterales.'
                              : formData.banner_divisions === 3
                              ? '3 espacios para banners inteligentes laterales.'
                              : '4 espacios para banners inteligentes laterales (30%, 20%, 20%, 30%).'
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'banners' && (
                <div className="space-y-4">
                  {formData.content_type === 'menu_only' ? (
                    <Alert variant="info">
                      Esta pantalla está configurada para mostrar solo menú; no requiere banners.
                    </Alert>
                  ) : formData.content_type === 'banners_only' ? (
                    <>
                      {/* Configuración de Divisiones para banners_only */}
                      <div>
                        <Select
                          label="División de Pantalla"
                          value={formData.banner_divisions.toString()}
                          onChange={(e) => {
                            const newDivisions = parseInt(e.target.value, 10);
                            setFormData({ ...formData, banner_divisions: newDivisions });
                            // Si cambia a 2 divisiones, validar que hay exactamente 2 combo banners
                            if (newDivisions === 2 && comboBanners.length !== 2) {
                              toast.warning('Para 2 divisiones necesitas configurar exactamente 2 combo banners');
                            }
                          }}
                          options={[
                            { value: '1', label: '1 División (Slider con timer)' },
                            { value: '2', label: '2 Divisiones (Banners lado a lado)' }
                          ]}
                          helperText={
                            formData.banner_divisions === 1
                              ? 'Los banners se mostrarán en un slider con bucle infinito y timer'
                              : 'Se mostrarán 2 banners simultáneamente, uno a la izquierda y otro a la derecha'
                          }
                        />
                      </div>

                      {/* Sección de Combo Banners - Solo para banners_only */}
                      <div>
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h4 className="text-md font-semibold text-gray-800">Combo Banners</h4>
                            <p className="text-sm text-gray-500 mt-1">
                              {formData.banner_divisions === 1
                                ? 'Configura los banners de combos que se mostrarán en un slider con bucle infinito. Puedes agregar múltiples banners.'
                                : 'Selecciona exactamente 2 combo banners. Uno se mostrará a la izquierda y otro a la derecha.'}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="primary"
                            onClick={() => {
                              if (!selectedEstablishmentId) {
                                toast.error('Debes seleccionar un establecimiento');
                                return;
                              }
                              if (formData.content_type !== 'banners_only') {
                                toast.error('Esta opción solo está disponible para pantallas de tipo "Solo Banners"');
                                return;
                              }
                              if (!editingScreen) {
                                toast.warning('Primero debes crear la pantalla para poder agregar combo banners. Completa el formulario y haz clic en "Crear Pantalla".');
                                return;
                              }
                              // No hay límite de banners cuando banner_divisions = 2, solo se seleccionan 2
                              setEditingComboBanner(null);
                              setShowComboBannerForm(true);
                            }}
                            disabled={
                              !selectedEstablishmentId || 
                              formData.content_type !== 'banners_only'
                            }
                          >
                            + Agregar Combo Banner
                          </Button>
                        </div>

                        {loadingComboBanners ? (
                          <div className="text-center py-8 text-gray-500">Cargando combo banners...</div>
                        ) : comboBanners.length === 0 ? (
                          <Alert variant="info">
                            {formData.banner_divisions === 1
                              ? 'No hay combo banners configurados. Agrega al menos un combo banner para mostrar en la pantalla.'
                              : 'No hay combo banners configurados. Agrega al menos 2 combo banners para mostrar en la pantalla dividida.'}
                          </Alert>
                        ) : (
                          <div className="space-y-4">
                            {formData.banner_divisions === 2 && (
                              <>
                                {comboBanners.filter(b => {
                                  const bSplitPos = b.split_position !== null && b.split_position !== undefined 
                                    ? b.split_position 
                                    : null;
                                  return bSplitPos === 1 || bSplitPos === 2;
                                }).length !== 2 && (
                                  <Alert variant="warning" className="mb-4">
                                    Para 2 divisiones debes seleccionar exactamente 2 combo banners. Actualmente tienes {comboBanners.filter(b => {
                                      const bSplitPos = b.split_position !== null && b.split_position !== undefined 
                                        ? b.split_position 
                                        : null;
                                      return bSplitPos === 1 || bSplitPos === 2;
                                    }).length} seleccionados de {comboBanners.length} disponibles.
                                  </Alert>
                                )}
                                <Alert variant="info" className="mb-4">
                                  Selecciona qué 2 combo banners se mostrarán lado a lado. Puedes tener más de 2 banners creados, pero solo se mostrarán los 2 seleccionados.
                                </Alert>
                              </>
                            )}
                            {comboBanners
                              .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                              .map((banner, index) => {
                                // Normalizar split_position para comparación
                                const splitPos = banner.split_position !== null && banner.split_position !== undefined 
                                  ? banner.split_position 
                                  : null;
                                const isSelectedForSplit = splitPos === 1 || splitPos === 2;
                                const selectedCount = comboBanners.filter(b => {
                                  const bSplitPos = b.split_position !== null && b.split_position !== undefined 
                                    ? b.split_position 
                                    : null;
                                  return bSplitPos === 1 || bSplitPos === 2;
                                }).length;
                                
                                // Debug log
                                if (formData.banner_divisions === 2) {
                                  console.log('🔍 [Screens] Banner renderizado:', {
                                    id: banner.id,
                                    split_position: splitPos,
                                    isSelectedForSplit,
                                    selectedCount,
                                    banner_divisions: formData.banner_divisions
                                  });
                                }
                                
                                return (
                                  <div
                                    key={banner.id || index}
                                    className={`border rounded-lg p-4 transition-shadow ${
                                      isSelectedForSplit && formData.banner_divisions === 2
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 bg-white hover:shadow-md'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                          {formData.banner_divisions === 2 && (
                                            <input
                                              type="checkbox"
                                              checked={isSelectedForSplit}
                                              onChange={(e) => {
                                                console.log('🔘 [Screens] Checkbox cambiado:', {
                                                  checked: e.target.checked,
                                                  bannerId: banner.id,
                                                  currentSelected: selectedCount
                                                });
                                                
                                                if (e.target.checked) {
                                                  // Contar los seleccionados actualmente
                                                  const currentSelected = comboBanners.filter(b => 
                                                    b.split_position === 1 || b.split_position === 2
                                                  ).length;
                                                  
                                                  // Si ya hay 2 seleccionados, no permitir más
                                                  if (currentSelected >= 2) {
                                                    toast.warning('Solo puedes seleccionar 2 banners para la pantalla dividida');
                                                    return;
                                                  }
                                                  
                                                  // Asignar posición: si no hay ninguno seleccionado, este será 1, si hay uno, este será 2
                                                  const newPosition = currentSelected === 0 ? 1 : 2;
                                                  
                                                  console.log('✅ [Screens] Asignando split_position:', {
                                                    bannerId: banner.id,
                                                    newPosition
                                                  });
                                                  
                                                  setComboBanners(prev => {
                                                    const updated = prev.map(b => 
                                                      b.id === banner.id 
                                                        ? { ...b, split_position: newPosition }
                                                        : b
                                                    );
                                                    console.log('📝 [Screens] Estado actualizado:', updated);
                                                    return updated;
                                                  });
                                                } else {
                                                  // Deseleccionar: limpiar split_position
                                                  console.log('❌ [Screens] Deseleccionando banner:', banner.id);
                                                  
                                                  setComboBanners(prev => {
                                                    const updated = prev.map(b => 
                                                      b.id === banner.id 
                                                        ? { ...b, split_position: null }
                                                        : b
                                                    );
                                                    console.log('📝 [Screens] Estado actualizado:', updated);
                                                    return updated;
                                                  });
                                                }
                                              }}
                                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                          )}
                                          <span className="text-sm font-semibold text-gray-700">
                                            #{banner.display_order || index + 1}
                                            {formData.banner_divisions === 2 && isSelectedForSplit && (
                                              <span className="ml-2 text-blue-600">
                                                ({splitPos === 1 ? 'Izquierda' : 'Derecha'})
                                              </span>
                                            )}
                                          </span>
                                          <span className="text-lg font-bold text-gray-800">
                                            <span style={{ color: banner.title_word1_color }}>
                                              {banner.title_word1}
                                            </span>{' '}
                                            <span style={{ color: banner.title_word2_color }}>
                                              {banner.title_word2}
                                            </span>
                                          </span>
                                          {banner.is_active === 0 && (
                                            <Badge variant="warning">Inactivo</Badge>
                                          )}
                                        </div>
                                        <div className="text-sm text-gray-600 mb-2">
                                          Producto: {banner.producto_nombre || 'N/A'}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                          {formData.banner_divisions === 1 && `Duración: ${banner.duration}s | `}
                                          Orden: {banner.display_order || index + 1}
                                        </div>
                                        {banner.combo_image_url && (
                                          <div className="mt-3">
                                            <img
                                              src={banner.combo_image_url}
                                              alt="Combo"
                                              className="h-24 object-contain rounded border border-gray-200 bg-gray-50 p-1"
                                            />
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex gap-2 ml-4">
                                        <Button
                                          type="button"
                                          variant="secondary"
                                          size="sm"
                                          onClick={() => {
                                            setEditingComboBanner(banner);
                                            setShowComboBannerForm(true);
                                          }}
                                        >
                                          Editar
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="secondary"
                                          size="sm"
                                          onClick={() => handleDuplicateComboBanner(banner)}
                                          title="Duplicar este combo banner"
                                        >
                                          Duplicar
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="secondary"
                                          size="sm"
                                          onClick={async () => {
                                            if (!window.confirm('¿Estás seguro de que deseas eliminar este combo banner?')) {
                                              return;
                                            }
                                            if (!token) return;
                                            try {
                                              const res = await apiFetch(
                                                'combo-banners/delete',
                                                {
                                                  method: 'POST',
                                                  body: JSON.stringify({ id: banner.id })
                                                },
                                                token
                                              );
                                              if (isNetworkErrorResponse(res)) {
                                                toast.error('Error de conexión');
                                                return;
                                              }
                                              const json = await parseJsonResponse(res);
                                              if (json.error) {
                                                toast.error(json.message || 'Error al eliminar');
                                                return;
                                              }
                                              toast.success('Combo banner eliminado');
                                              if (editingScreen) {
                                                await loadScreenComboBanners(editingScreen.id);
                                              }
                                            } catch (err: any) {
                                              toast.error(err.message || 'Error al eliminar');
                                            }
                                          }}
                                        >
                                          Eliminar
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Banners Superiores - 2 para verticales, 1 para horizontales */}
                      {formData.orientation === 'vertical' ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Banners Superiores (2 banners, uno debajo del otro)
                          </label>
                          <div className="space-y-3">
                            {[0, 1].map((index) => (
                              <Select
                                key={index}
                                label={`Banner Superior ${index + 1}`}
                                value={selectedBanners.superior[index]?.toString() || ''}
                                onChange={(e) => {
                                  const bannerId = e.target.value ? parseInt(e.target.value, 10) : null;
                                  const newSuperior = [...selectedBanners.superior];
                                  
                                  // Si se selecciona un banner que ya está en el otro slot, limpiar el otro slot
                                  if (bannerId) {
                                    const otherIndex = index === 0 ? 1 : 0;
                                    if (selectedBanners.superior[otherIndex] === bannerId) {
                                      newSuperior[otherIndex] = null;
                                    }
                                  }
                                  
                                  newSuperior[index] = bannerId;
                                  setSelectedBanners({
                                    ...selectedBanners,
                                    superior: newSuperior
                                  });
                                }}
                                options={[
                                  { value: '', label: `-- Sin banner superior ${index + 1} --` },
                                  ...availableBanners
                                    .filter((banner) => {
                                      // Filtrar banners que ya están seleccionados en el otro slot
                                      const otherIndex = index === 0 ? 1 : 0;
                                      const otherSelectedId = selectedBanners.superior[otherIndex];
                                      return !otherSelectedId || banner.id !== otherSelectedId;
                                    })
                                    .map((banner) => ({
                                      value: banner.id.toString(),
                                      label: `${banner.name} ${banner.is_active === 1 ? '✓' : '(Inactivo)'}`
                                    }))
                                ]}
                                disabled={loadingBanners}
                                helperText={`Banner tipo imagen que se mostrará en la parte superior ${index + 1} de la pantalla vertical.`}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Select
                            label="Banner Superior"
                            value={selectedBanners.superior[0]?.toString() || ''}
                            onChange={(e) => {
                              const bannerId = e.target.value ? parseInt(e.target.value, 10) : null;
                              setSelectedBanners({
                                ...selectedBanners,
                                superior: [bannerId, null]
                              });
                            }}
                            options={[
                              { value: '', label: '-- Sin banner superior --' },
                              ...availableBanners.map((banner) => ({
                                value: banner.id.toString(),
                                label: `${banner.name} ${banner.is_active === 1 ? '✓' : '(Inactivo)'}`
                              }))
                            ]}
                            disabled={loadingBanners}
                            helperText={
                              formData.products_count === 6
                                ? 'Banner tipo imagen que se mostrará en la parte superior de la pantalla, encima del menú de productos. Los banners laterales solo están disponibles para pantallas de 10 productos.'
                                : 'Banner tipo imagen que se mostrará en la parte superior de la pantalla, encima del menú de productos.'
                            }
                          />
                        </div>
                      )}

                      {/* Banners Laterales - Cuando products_count es 10 O cuando es 6 con banner_divisions === 2 */}
                      {(formData.products_count === 10 || 
                        (formData.products_count === 6 && formData.banner_divisions === 2)) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Banners Laterales ({formData.banner_divisions}{' '}
                            {formData.banner_divisions === 1 ? 'espacio' : 'espacios'})
                          </label>
                          <div className="space-y-3">
                            {Array.from({ length: formData.banner_divisions }).map((_, index) => {
                              const laterales = [...selectedBanners.laterales];
                              while (laterales.length < formData.banner_divisions) {
                                laterales.push(null);
                              }
                              
                              return (
                                <Select
                                  key={index}
                                  label={`Banner Lateral ${index + 1}`}
                                  value={laterales[index]?.toString() || ''}
                                  onChange={(e) => {
                                    const bannerId = e.target.value ? parseInt(e.target.value, 10) : null;
                                    const newLaterales = [...laterales];
                                    newLaterales[index] = bannerId;
                                    setSelectedBanners({
                                      ...selectedBanners,
                                      laterales: newLaterales
                                    });
                                  }}
                                  options={[
                                    { value: '', label: `-- Sin banner lateral ${index + 1} --` },
                                    ...availableBanners.map((banner) => ({
                                      value: banner.id.toString(),
                                      label: `${banner.name} ${banner.is_active === 1 ? '✓' : '(Inactivo)'}`
                                    }))
                                  ]}
                                  disabled={loadingBanners}
                                  helperText={`Banner tipo imagen que se mostrará en el lado ${formData.banner_side === 'left' ? 'izquierdo' : 'derecho'} de la pantalla, posición ${index + 1}.`}
                                />
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {availableBanners.length === 0 && !loadingBanners && (
                        <Alert variant="warning">
                          No hay banners disponibles para este establecimiento. Crea banners primero en la sección de
                          Banners.
                        </Alert>
                      )}

                      {/* Banners Inteligentes - Solo cuando products_count es 6 Y banner_divisions es 3 o 4 */}
                      {formData.products_count === 6 && (formData.banner_divisions === 3 || formData.banner_divisions === 4) && (
                      <div className="border-t border-gray-200 pt-6 mt-6">
                        <h4 className="text-md font-semibold text-gray-800 mb-4">Banners Inteligentes</h4>
                        <p className="text-sm text-gray-500 mb-4">
                          Selecciona banners inteligentes para mostrar en esta pantalla. Los banners inteligentes pueden contener productos y contenido personalizado.
                            <strong className="block mt-2 text-amber-600">
                              Nota: Los banners inteligentes solo están disponibles cuando la pantalla muestra 6 productos.
                            </strong>
                        </p>

                        <div className="space-y-4">
                          <Select
                            label="Banner Inteligente Superior"
                            value={selectedSmartBanners.superior?.toString() || ''}
                            onChange={(e) => {
                              const smartBannerId = e.target.value ? parseInt(e.target.value, 10) : null;
                              setSelectedSmartBanners({
                                ...selectedSmartBanners,
                                superior: smartBannerId
                              });
                            }}
                            options={[
                              { value: '', label: '-- Sin banner inteligente superior --' },
                              ...availableSmartBanners.map((smartBanner) => ({
                                value: smartBanner.id.toString(),
                                  label: `${smartBanner.name} ${smartBanner.is_active === 1 ? '✓' : '(Inactivo)'}`
                              }))
                            ]}
                            disabled={loadingSmartBanners}
                              helperText="Banner inteligente que se mostrará en la parte superior. Puede contener productos personalizados, extras, bebidas o chips según su configuración."
                          />

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Banners Inteligentes Laterales ({formData.banner_divisions} espacios)
                              </label>
                              <div className="space-y-3">
                                {Array.from({ length: formData.banner_divisions }).map((_, index) => {
                                  const laterales = [...selectedSmartBanners.laterales];
                                  while (laterales.length < formData.banner_divisions) {
                                    laterales.push(null);
                                  }
                                  
                                  return (
                                    <Select
                                      key={index}
                                      label={`Banner Inteligente Lateral ${index + 1}`}
                                      value={laterales[index]?.toString() || ''}
                                      onChange={(e) => {
                                        const smartBannerId = e.target.value ? parseInt(e.target.value, 10) : null;
                                        const newLaterales = [...laterales];
                                        newLaterales[index] = smartBannerId;
                                        setSelectedSmartBanners({
                                          ...selectedSmartBanners,
                                          laterales: newLaterales
                                        });
                                      }}
                                      options={[
                                        { value: '', label: `-- Sin banner inteligente lateral ${index + 1} --` },
                                        ...availableSmartBanners.map((smartBanner) => ({
                                          value: smartBanner.id.toString(),
                                          label: `${smartBanner.name} ${smartBanner.is_active === 1 ? '✓' : '(Inactivo)'}`
                                        }))
                                      ]}
                                      disabled={loadingSmartBanners}
                                      helperText={`Banner inteligente que se mostrará en el lado ${formData.banner_side === 'left' ? 'izquierdo' : 'derecho'} de la pantalla, posición ${index + 1}. Puede contener productos personalizados según su configuración.`}
                                    />
                                  );
                                })}
                              </div>
                            </div>

                          {availableSmartBanners.length === 0 && !loadingSmartBanners && (
                            <Alert variant="info">
                              No hay banners inteligentes disponibles para este establecimiento. Crea banners inteligentes primero en la sección de Smart Banners.
                            </Alert>
                          )}
                        </div>
                      </div>
                      )}

                      {/* Mensaje informativo según configuración */}
                      {formData.products_count === 10 && (
                        <Alert variant="info" className="mt-6">
                          <strong>Nota:</strong> Cuando la pantalla muestra 10 productos, solo se pueden configurar banners normales (imágenes). Los banners inteligentes solo están disponibles para pantallas con 6 productos.
                    </Alert>
                      )}
                      {formData.products_count === 6 && formData.banner_divisions === 0 && (
                        <Alert variant="info" className="mt-6">
                          <strong>Nota:</strong> Sin banners laterales. Los productos ocuparán todo el ancho de la pantalla. Solo se mostrará el banner superior si está configurado.
                        </Alert>
                      )}
                      {formData.products_count === 6 && formData.banner_divisions === 2 && (
                        <Alert variant="info" className="mt-6">
                          <strong>Nota:</strong> Con 6 productos y 2 divisiones, puedes configurar banners normales (imágenes). Si deseas usar banners inteligentes, cambia las divisiones a 3.
                        </Alert>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'styles' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-start mb-4">
                  <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">Configuración Visual</h3>
                      <p className="text-sm text-gray-500">
                      Personaliza los colores y fuentes que se mostrarán en esta pantalla.
                    </p>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={async () => {
                        if (!selectedEstablishmentId) {
                          toast.error('Debes seleccionar un establecimiento primero');
                          return;
                        }
                        setShowCopyStylesModal(true);
                        await loadScreensForCopy();
                      }}
                      className="whitespace-nowrap"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copiar Estilos
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ColorPicker
                      label="Color de Fondo"
                      value={formData.color_background}
                      onChange={(e) => setFormData({ ...formData, color_background: e.target.value })}
                    />

                    <ColorPicker
                      label="Color de Títulos"
                      value={formData.color_title}
                      onChange={(e) => setFormData({ ...formData, color_title: e.target.value })}
                    />

                    <ColorPicker
                      label="Color de Subtítulos"
                      value={formData.color_subtitle}
                      onChange={(e) => setFormData({ ...formData, color_subtitle: e.target.value })}
                    />

                    <ColorPicker
                      label="Color de Textos"
                      value={formData.color_text}
                      onChange={(e) => setFormData({ ...formData, color_text: e.target.value })}
                    />

                    <ColorPicker
                      label="Color del Texto de Precios (Regular/Gigante)"
                      value={formData.color_price_text}
                      onChange={(e) => setFormData({ ...formData, color_price_text: e.target.value })}
                    />

                    <ColorPicker
                      label="Color de los Números de Precios"
                      value={formData.color_price_number}
                      onChange={(e) => setFormData({ ...formData, color_price_number: e.target.value })}
                    />
                  </div>

                  <div className="border-t border-gray-200 pt-6 mt-6">
                    <h4 className="text-md font-semibold text-gray-800 mb-4">Fuentes</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Select
                        label="Fuente para Títulos"
                        value={formData.font_title_id?.toString() || ''}
                        onChange={(e) => setFormData({ ...formData, font_title_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                        options={[
                          { value: '', label: 'Sin fuente' },
                          ...fonts.map(f => ({ value: f.id.toString(), label: f.display_name }))
                        ]}
                        disabled={loadingFonts}
                      />

                      <Select
                        label="Fuente para Subtítulos"
                        value={formData.font_subtitle_id?.toString() || ''}
                        onChange={(e) => setFormData({ ...formData, font_subtitle_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                        options={[
                          { value: '', label: 'Sin fuente' },
                          ...fonts.map(f => ({ value: f.id.toString(), label: f.display_name }))
                        ]}
                        disabled={loadingFonts}
                      />

                      <Select
                        label="Fuente para Textos"
                        value={formData.font_text_id?.toString() || ''}
                        onChange={(e) => setFormData({ ...formData, font_text_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                        options={[
                          { value: '', label: 'Sin fuente' },
                          ...fonts.map(f => ({ value: f.id.toString(), label: f.display_name }))
                        ]}
                        disabled={loadingFonts}
                      />

                      <Select
                        label="Fuente para Precios (Script)"
                        value={formData.font_script_id?.toString() || ''}
                        onChange={(e) => setFormData({ ...formData, font_script_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                        options={[
                          { value: '', label: 'Sin fuente' },
                          ...fonts.map(f => ({ value: f.id.toString(), label: f.display_name }))
                        ]}
                        disabled={loadingFonts}
                      />

                      <Select
                        label="Fuente para Texto de Precios (Regular/Gigante)"
                        value={formData.font_price_text_id?.toString() || ''}
                        onChange={(e) => setFormData({ ...formData, font_price_text_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                        options={[
                          { value: '', label: 'Sin fuente' },
                          ...fonts.map(f => ({ value: f.id.toString(), label: f.display_name }))
                        ]}
                        disabled={loadingFonts}
                      />

                      <Select
                        label="Fuente para Números de Precios"
                        value={formData.font_price_number_id?.toString() || ''}
                        onChange={(e) => setFormData({ ...formData, font_price_number_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                        options={[
                          { value: '', label: 'Sin fuente' },
                          ...fonts.map(f => ({ value: f.id.toString(), label: f.display_name }))
                        ]}
                        disabled={loadingFonts}
                      />
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-6 mt-6">
                    <h4 className="text-md font-semibold text-gray-800 mb-4">Tamaños de Fuente</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <Input
                        type="number"
                        label="Tamaño Fuente Títulos"
                        value={formData.font_size_title.toString()}
                        onChange={(e) => setFormData({ ...formData, font_size_title: parseInt(e.target.value, 10) || 24 })}
                        min="8"
                        max="100"
                      />

                      <Input
                        type="number"
                        label="Tamaño Fuente Subtítulos"
                        value={formData.font_size_subtitle.toString()}
                        onChange={(e) => setFormData({ ...formData, font_size_subtitle: parseInt(e.target.value, 10) || 18 })}
                        min="8"
                        max="72"
                      />

                      <Input
                        type="number"
                        label="Tamaño Fuente Textos"
                        value={formData.font_size_text.toString()}
                        onChange={(e) => setFormData({ ...formData, font_size_text: parseInt(e.target.value, 10) || 14 })}
                        min="8"
                        max="72"
                      />

                      <Input
                        type="number"
                        label="Tamaño Fuente Texto de Precios"
                        value={formData.font_size_price_text.toString()}
                        onChange={(e) => setFormData({ ...formData, font_size_price_text: parseInt(e.target.value, 10) || 16 })}
                        min="8"
                        max="72"
                      />

                      <Input
                        type="number"
                        label="Tamaño Fuente Números de Precios"
                        value={formData.font_size_price_number.toString()}
                        onChange={(e) => setFormData({ ...formData, font_size_price_number: parseInt(e.target.value, 10) || 16 })}
                        min="8"
                        max="72"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'slider-images' && (
                <div className="space-y-4">
                  <SliderImageManager
                    screenId={editingScreen?.id || null}
                    establishmentId={parseInt(selectedEstablishmentId) || 0}
                    token={token || ''}
                  />
                </div>
              )}

              {activeTab === 'products' && (
                <div className="space-y-4">
                  {isMenuContent ? (
                    <>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">Productos</h3>
                          <p className="text-sm text-gray-500">Define el contenido que se mostrará en la pantalla.</p>
                        </div>
                        <Badge variant="info">
                          {selectedReferences.length}
                          {productsLimit !== null ? ` / ${productsLimit}` : ''}
                        </Badge>
                      </div>

                      <Input
                        label="Buscar productos"
                        type="text"
                        placeholder="Busca por nombre, familia o categoría"
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                      />

                      {loadingProducts ? (
                        <div className="text-sm text-gray-500">Cargando productos disponibles...</div>
                      ) : availableReferences.length === 0 ? (
                        <Alert variant="warning">
                          No se encontraron productos disponibles para este establecimiento.
                        </Alert>
                      ) : (
                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Disponibles</h4>
                            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                          {filteredAvailableProducts.map((reference, referenceIndex) => {
                                const alreadySelected = selectedReferences.some(
                                  (p) => p.reference_code === reference.reference_code
                                );
                                const limitReached =
                                  productsLimit !== null && selectedReferences.length >= productsLimit && !alreadySelected;
                                return (
                                  <div
                                key={`${reference.reference_code || reference.base_name || 'reference'}-${referenceIndex}`}
                                    className="border border-gray-200 rounded-lg p-3 bg-white shadow-sm flex items-start justify-between gap-3"
                                  >
                                    <div className="flex-1">
                                      <p className="font-semibold text-gray-900">{reference.base_name}</p>
                                      {reference.family && (
                                        <p className="text-xs text-gray-500">{reference.family}</p>
                                      )}
                                      <div className="text-xs text-gray-600 mt-2 space-y-1">
                                        {reference.variants.map((variant) => (
                                      <div
                                        key={`${reference.reference_code || 'ref'}-${variant.product_id}-${referenceIndex}`}
                                        className="flex items-center justify-between gap-4"
                                      >
                                            <span>{variant.variant_label}</span>
                                            <span className="font-semibold text-gray-800">
                                              {formatPrice(variant.price)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600"
                                      disabled={alreadySelected || limitReached}
                                      onClick={() => handleAddProduct(reference)}
                                    >
                                      {alreadySelected ? 'Agregado' : 'Agregar'}
                                    </Button>
                                  </div>
                                );
                              })}
                              {filteredAvailableProducts.length === 0 && (
                                <p className="text-sm text-gray-500">
                                  No hay productos que coincidan con la búsqueda.
                                </p>
                              )}
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Seleccionados</h4>
                            {selectedReferences.length === 0 ? (
                              <p className="text-sm text-gray-500">
                                Aún no has seleccionado productos para esta pantalla.
                              </p>
                            ) : (
                              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                            {selectedReferences.map((reference, index) => (
                                  <div
                                key={`${reference.reference_code || reference.base_name || 'selected'}-${index}`}
                                    className="border border-red-100 rounded-lg p-3 bg-white shadow-sm"
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex-1">
                                        <p className="font-semibold text-red-700">
                                          {index + 1}. {reference.base_name}
                                        </p>
                                        <div className="text-xs text-gray-600 mt-2 space-y-1">
                                      {reference.variants.map((variant, variantIndex) => (
                                        <div
                                          key={`${reference.reference_code || 'selected'}-${variant.product_id}-${variantIndex}`}
                                          className="flex justify-between gap-4"
                                        >
                                              <span>{variant.variant_label}</span>
                                              <span className="font-semibold text-gray-800">
                                                {formatPrice(variant.price)}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                      <div className="flex flex-col gap-2">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          disabled={index === 0}
                                          onClick={() => moveProduct(index, 'up')}
                                        >
                                          ↑
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          disabled={index === selectedReferences.length - 1}
                                          onClick={() => moveProduct(index, 'down')}
                                        >
                                          ↓
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-red-600"
                                          onClick={() => handleRemoveProduct(reference.reference_code)}
                                        >
                                          Quitar
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Alert variant="info">
                      Esta pantalla no muestra menú, por lo que no requiere selección de productos.
                    </Alert>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                {editingScreen && (
                  <Button
                    variant="ghost"
                    size="md"
                    onClick={handleForceRefresh}
                    disabled={submitting || loading}
                    className="flex-1 sm:flex-none order-first sm:order-none"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Forzar Refresh
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleSubmit}
                  disabled={submitting || loading}
                  isLoading={submitting}
                  className="flex-1"
                >
                  {editingScreen ? 'Actualizar Pantalla' : 'Crear Pantalla'}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </Sheet>

          {/* Modal para copiar estilos de otra pantalla */}
          {showCopyStylesModal && (
            <Sheet
              isOpen={showCopyStylesModal}
              onClose={() => setShowCopyStylesModal(false)}
              title="Copiar Estilos de Otra Pantalla"
              size="md"
            >
              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Selecciona una pantalla para copiar sus estilos (colores, fuentes y tamaños). Los estilos se aplicarán a esta pantalla y podrás editarlos después.
                </p>
                
                {loadingScreensForCopy ? (
                  <div className="text-center py-8 text-gray-500">Cargando pantallas...</div>
                ) : availableScreensForCopy.length === 0 ? (
                  <Alert variant="warning">
                    No hay otras pantallas disponibles en este establecimiento para copiar estilos.
                  </Alert>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {availableScreensForCopy.map((screen) => (
                      <Card
                        key={screen.id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleCopyStyles(screen)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-800">{screen.name}</h4>
                              <p className="text-xs text-gray-500 mt-1">
                                {screen.orientation === 'horizontal' ? 'Horizontal' : 'Vertical'} • {' '}
                                {screen.content_type === 'menu_only' ? 'Solo Menú' :
                                 screen.content_type === 'banners_only' ? 'Solo Banners' :
                                 'Menú y Banners'}
                              </p>
                            </div>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyStyles(screen);
                              }}
                            >
                              Copiar
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </Sheet>
          )}

          {/* Modal de Combo Banner Form - Con z-index mayor para estar por encima del Sheet principal */}
          {showComboBannerForm && editingScreen && (
            <Sheet
              isOpen={showComboBannerForm}
              onClose={() => {
                setShowComboBannerForm(false);
                setEditingComboBanner(null);
              }}
              title={editingComboBanner ? 'Editar Combo Banner' : 'Nuevo Combo Banner'}
              zIndex={60}
            >
              <div className="p-6">
                <ComboBannerForm
                  comboBanner={editingComboBanner}
                  pantallaId={editingScreen.id}
                  establishmentId={selectedEstablishmentId}
                  availableProducts={availableReferences}
                  bannerDivisions={formData.banner_divisions}
                  orientation={editingScreen.orientation}
                  onSuccess={async () => {
                    setShowComboBannerForm(false);
                    setEditingComboBanner(null);
                    if (editingScreen) {
                      await loadScreenComboBanners(editingScreen.id);
                    }
                  }}
                  onCancel={() => {
                    setShowComboBannerForm(false);
                    setEditingComboBanner(null);
                  }}
                />
              </div>
            </Sheet>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

